import { useTranslation } from "react-i18next"

import { useUserSettings } from "@/contexts"
import type { SelectableLanguageCode } from "@/models/i18n"
import { translateDecimal } from "@/utils/decimal"
import type { DecimalFormatOptions, DecimalOptions } from "@/utils/decimal"

export interface DecimalProps extends DecimalOptions {
  format?: DecimalFormatOptions
  value: number | string | null | undefined
}

/**
 * A component that properly formats and internationalizes the provided decimal
 *
 * @param props - The component properties
 * @param props.value - The decimal value
 * @param props.format - The format configuration for the decimal
 * @param props.placeholder - The placeholder to render instead of the decimal
 * @param props.replaceZeroWithPlaceholder - Whether to consider 0 as an empty value
 * @param props.useAccountingFormatWhenNegative - Whether to wrap negative decimals in parenthesis or not
 * @example
 * return (
 *   <Decimal value={waterUse} format={{
 *     precision: 2
 *   }} />
 * )
 */
export const Decimal = ({
  format,
  placeholder,
  replaceZeroWithPlaceholder,
  unit,
  useAccountingFormatWhenNegative = true,
  value,
}: DecimalProps) => {
  const { i18n } = useTranslation()
  const { unitsSystem, currency } = useUserSettings()

  return (
    <>
      {translateDecimal({
        format,
        localeCode: i18n.language as SelectableLanguageCode,
        options: {
          currencyCode: currency,
          placeholder,
          replaceZeroWithPlaceholder,
          unit,
          unitsSystem,
          useAccountingFormatWhenNegative,
        },
        value,
      })}
    </>
  )
}
